import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Destination, DestinationsStorageApiClient } from '../../services/DestinationsApiClient';
import { Button, styled } from '@streamelements/frontend-ui';
import { streamDestinationsQueryOptions } from '../../api/streamDestinations';

const DisconnectActionContainer = styled('div', {
  display: 'flex',
  gap: '0.5rem',
  position: 'relative',

  '[data-connected], [data-disconnect]': {
    width: '100%',
    transition: 'opacity 0.25s $1',
  },

  '[data-connected]': {
    pointerEvents: 'none',
    position: 'absolute',
    top: '50%',
    right: 0,
    transform: 'translateY(-50%)',
  },

  '[data-disconnect]': {
    opacity: 0,
  },

  '&:hover': {
    '[data-connected]': {
      opacity: 0,
    },
    '[data-disconnect]': {
      opacity: 1,
    },
  },
});

export function useDisconnectDestination(params: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  destinationMetadata: Destination;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const destinationsApiClient = new DestinationsStorageApiClient({
        authContextProvider: params.authContextProvider,
        serviceEnvironment: params.serviceEnvironment,
      });

      return destinationsApiClient.removeDestinationById({
        id: params.destinationMetadata.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: streamDestinationsQueryOptions({
          authContextProvider: params.authContextProvider,
          serviceEnvironment: params.serviceEnvironment,
        }).queryKey,
      });
    },
  });
}

export function DisconnectButton({
  authContextProvider,
  serviceEnvironment,
  destinationMetadata,
  disabled,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  destinationMetadata: Destination;
  disabled?: boolean;
}) {
  const disconnectMutation = useDisconnectDestination({
    authContextProvider,
    serviceEnvironment,
    destinationMetadata,
  });

  if (disabled) {
    return (
      <Button variant="ghost" density="compact" data-connected disabled={disconnectMutation.isPending || disabled}>
        {disconnectMutation.isPending ? 'Disconnecting' : 'Connected'}
      </Button>
    );
  }

  return (
    <DisconnectActionContainer>
      <Button variant="ghost" density="compact" data-connected disabled={disconnectMutation.isPending}>
        {disconnectMutation.isPending ? 'Disconnecting' : 'Connected'}
      </Button>
      <Button
        disabled={disconnectMutation.isPending}
        variant="ghost"
        density="compact"
        color="error"
        data-disconnect
        onClick={() => disconnectMutation.mutate()}
      >
        {disconnectMutation.isPending ? 'Disconnecting' : 'Disconnect'}
      </Button>
    </DisconnectActionContainer>
  );
}
